import { ShortCode } from '@highereducation/wordpress';
import { Grid } from '@highereducation/hen-ui';

interface GridShortcodeProps {
  columns?: '1' | '2' | '3' | '4' | '5' | '6';
  'columns-md'?: '1' | '2' | '3' | '4' | '5' | '6';
  'columns-lg'?: '1' | '2' | '3' | '4' | '5' | '6';
  layout?: 'feature';
  gap?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'max';
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * GridShortcode - Shortcode wrapper for Grid layout component
 *
 * Responsive multi-column grid layout.
 * Replaces manual `<div class="grid grid-cols-* gap-*">` patterns.
 *
 * Supported shortcode attributes:
 * - columns: '1' | '2' | '3' | '4' | '5' | '6' (default: '1')
 * - columns-md: '1' | '2' | '3' | '4' | '5' | '6' (columns at md breakpoint)
 * - columns-lg: '1' | '2' | '3' | '4' | '5' | '6' (columns at lg breakpoint)
 * - gap: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'max' (default: 'lg')
 *
 * @example WordPress shortcode usage:
 * [hen-ui-grid columns="1" columns-md="2" columns-lg="4" gap="lg"]
 *   [hen-ui-image-card ...]...[/hen-ui-image-card]
 *   [hen-ui-image-card ...]...[/hen-ui-image-card]
 *   [hen-ui-image-card ...]...[/hen-ui-image-card]
 *   [hen-ui-image-card ...]...[/hen-ui-image-card]
 * [/hen-ui-grid]
 */
export const GridShortcode: ShortCode<GridShortcodeProps> = {
  balanced: true,
  component: ({
    columns = '1',
    'columns-md': columnsMd,
    'columns-lg': columnsLg,
    layout,
    gap = 'lg',
    children,
  }) => {
    const parseColumns = (val: string | undefined) =>
      val ? (parseInt(val, 10) as 1 | 2 | 3 | 4 | 5 | 6) : undefined;

    return (
      <Grid
        columns={parseColumns(columns)}
        columns-md={parseColumns(columnsMd)}
        columns-lg={parseColumns(columnsLg)}
        layout={layout}
        gap={gap}
      >
        {children}
      </Grid>
    );
  },
};
